// Pure helpers for the Workshop 4 dashboard (mirrors workshop1Data.js style).
// The canonical 1-4 scale is reused from backend/routes/dashboardRoutes.js:
// severityMap / likelihoodMap: Faible -> 1, Moyenne -> 2, Élevée -> 3, Critique -> 4.

export const W4_LEVEL_LABELS = ['Faible', 'Moyenne', 'Élevée', 'Critique'];

export const W4_SKILL_ORDER = ['Débutant', 'Intermédiaire', 'Avancé', 'Expert'];

const stripAccents = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

// Extended level normalizer. Recognizes the canonical FR 1-4 scale
// (Faible/Moyenne/Élevée/Critique), the EN 5-level severity codes stored by
// dreaded events and CTI (minor/moderate/major/critical/catastrophic, plus the
// FR labels Mineure/Modérée/Majeure/Catastrophique), masculine/feminine
// variants (Moyen/Moyenne, Élevé/Élevée), numeric inputs and G/E/niveau codes.
// Anything unmapped (including empty) falls back to 3 — parity with the
// backend `|| 3` default in backend/routes/dashboardRoutes.js — and is flagged
// via `defaulted` so the matrix can mark those cells with an outline marker.
const LEVEL_KEYWORDS = [
  { level: 1, keys: ['faible', 'tres faible', 'very low', 'low', 'minor', 'mineur', 'mineure', 'negligeable', 'minimal'] },
  { level: 2, keys: ['moyen', 'moyenne', 'modere', 'moderee', 'medium', 'moderate'] },
  { level: 3, keys: ['eleve', 'elevee', 'high', 'major', 'majeur', 'majeure'] },
  { level: 4, keys: ['tres eleve', 'tres elevee', 'critique', 'critical', 'very high', 'catastrophique', 'catastrophic'] }
];

const UNKNOWN_LEVEL_TOKENS = ['non defini', 'undefined', 'unknown', 'n/a', 'na', '-', 'non evalue', 'not assessed', 'not evaluated', 'tbd', '?', 'none', 'aucun'];

export const normalizeW4LevelDetailed = (raw) => {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const numeric = Math.trunc(raw);
    if (numeric >= 1 && numeric <= 4) {
      return { level: numeric, defaulted: false };
    }
    if (numeric === 5) {
      return { level: 4, defaulted: false };
    }
    return { level: 3, defaulted: true };
  }
  const text = stripAccents(raw).trim().toLowerCase();
  if (!text || UNKNOWN_LEVEL_TOKENS.includes(text)) {
    return { level: 3, defaulted: true };
  }
  // Numeric codes: '3', 'g3', 'e2', 'n4', 'niveau 3', 'level 2'
  const codeMatch = text.match(/^(?:[gen]|lvl|level|niveau|gravite|gravity|echelle)?\s*([0-9]+)$/);
  if (codeMatch) {
    const numeric = parseInt(codeMatch[1], 10);
    if (numeric >= 1 && numeric <= 4) {
      return { level: numeric, defaulted: false };
    }
    if (numeric === 5) {
      return { level: 4, defaulted: false };
    }
    return { level: 3, defaulted: true };
  }
  // Drop parentheticals / suffixes ('elevee (50-80%)' -> 'elevee')
  const core = text.split(/[(:;]/)[0].split(/ [-–—]/)[0].trim();
  const hit = LEVEL_KEYWORDS.find((entry) => entry.keys.includes(core));
  if (hit) {
    return { level: hit.level, defaulted: false };
  }
  return { level: 3, defaulted: true };
};

export const normalizeW4Level = (raw) => normalizeW4LevelDetailed(raw).level;

export const levelToLabel = (level) => W4_LEVEL_LABELS[level - 1] || '';

export const normalizeSkill = (raw) => {
  const normalized = stripAccents(raw).trim().toLowerCase();
  if (!normalized || normalized === 'non defini' || normalized === 'undefined' || normalized === 'unknown') {
    return '';
  }
  if (normalized === 'debutant' || normalized === 'beginner') {
    return 'Débutant';
  }
  if (normalized === 'intermediaire' || normalized === 'intermediate') {
    return 'Intermédiaire';
  }
  if (normalized === 'avance' || normalized === 'advanced') {
    return 'Avancé';
  }
  if (normalized === 'expert') {
    return 'Expert';
  }
  return String(raw || '').trim();
};

const resolveMatrixCell = (item) => {
  const rawX = Number.isInteger(item?.x) ? item.x : null;
  const rawY = Number.isInteger(item?.y) ? item.y : null;
  const resolvedX = rawX !== null
    ? { level: rawX, defaulted: false }
    : normalizeW4LevelDetailed(item?.likelihood ?? item?.vraisemblance);
  const resolvedY = rawY !== null
    ? { level: rawY, defaulted: false }
    : normalizeW4LevelDetailed(item?.severity ?? item?.gravity ?? item?.gravite);
  if (resolvedX.level < 1 || resolvedX.level > 4 || resolvedY.level < 1 || resolvedY.level > 4) {
    return null;
  }
  return { x: resolvedX.level, y: resolvedY.level, defaulted: resolvedX.defaulted || resolvedY.defaulted };
};

export const buildRiskMatrix = (items) => {
  const list = Array.isArray(items) ? items : [];
  const counts = {};
  const defaultedKeys = new Set();
  let max = 0;
  let total = 0;
  let defaultedCount = 0;
  list.forEach((item) => {
    const cell = resolveMatrixCell(item);
    if (!cell) {
      return;
    }
    const key = `${cell.x}|${cell.y}`;
    counts[key] = (counts[key] || 0) + 1;
    if (cell.defaulted) {
      defaultedKeys.add(key);
      defaultedCount += 1;
    }
    if (counts[key] > max) {
      max = counts[key];
    }
    total += 1;
  });
  const data = [];
  for (let y = 1; y <= 4; y += 1) {
    for (let x = 1; x <= 4; x += 1) {
      const value = counts[`${x}|${y}`] || 0;
      if (defaultedKeys.has(`${x}|${y}`)) {
        data.push({
          value: [x - 1, y - 1, value],
          defaulted: true,
          itemStyle: { borderColor: '#1F2937', borderWidth: 2, borderType: 'dashed' }
        });
      } else {
        data.push([x - 1, y - 1, value]);
      }
    }
  }
  return {
    xLabels: [...W4_LEVEL_LABELS],
    yLabels: [...W4_LEVEL_LABELS],
    data,
    max,
    total,
    defaultedCount
  };
};

export const selectTopAssets = (assets, limit = 5) => {
  const list = Array.isArray(assets) ? assets : [];
  const result = [...list]
    .sort((a, b) => (
      (b?.riskScore || 0) - (a?.riskScore || 0)
      || (b?.vulnerabilityCount || 0) - (a?.vulnerabilityCount || 0)
    ))
    .slice(0, limit);
  result.total = list.length;
  return result;
};

export const deriveTechniquesFromScenarios = (operationalScenarios) => {
  const scenarios = Array.isArray(operationalScenarios) ? operationalScenarios : [];
  const techniquesMap = new Map();
  scenarios.forEach((scenario) => {
    const steps = Array.isArray(scenario?.operationalSteps) ? scenario.operationalSteps : [];
    steps.forEach((step) => {
      const technique = step?.technique;
      if (technique && technique.trim() !== '' && technique !== 'Inutile dans ce scénario') {
        const current = techniquesMap.get(technique) || { name: technique, count: 0 };
        current.count += 1;
        techniquesMap.set(technique, current);
      }
    });
  });
  return [...techniquesMap.values()].sort((a, b) => b.count - a.count);
};

export const selectTopTechniques = (techniques, fallbackScenarios, limit = 5) => {
  const source = Array.isArray(techniques) && techniques.length > 0
    ? techniques
    : deriveTechniquesFromScenarios(fallbackScenarios);
  const filtered = [...source]
    .map((item) => ({
      name: item?.name || item?.technique || '',
      count: item?.count ?? item?.frequency ?? 1,
      ...item
    }))
    .filter((item) => item.name !== '')
    .sort((a, b) => b.count - a.count);
  const result = filtered.slice(0, limit);
  result.total = filtered.length;
  return result;
};

export const buildSeveritySkillMatrix = (operationalScenarios) => {
  const scenarios = Array.isArray(operationalScenarios) ? operationalScenarios : [];
  const presentSkills = [];
  scenarios.forEach((scenario) => {
    const skill = normalizeSkill(scenario?.requiredSkills);
    if (skill && !presentSkills.includes(skill)) {
      presentSkills.push(skill);
    }
  });
  const columnLabels = W4_SKILL_ORDER.filter((skill) => presentSkills.includes(skill));
  const data = [];
  let max = 0;
  let total = 0;
  W4_LEVEL_LABELS.forEach((severityLabel, rowIndex) => {
    columnLabels.forEach((skill, colIndex) => {
      const count = scenarios.filter((scenario) => (
        levelToLabel(normalizeW4Level(scenario?.severity ?? scenario?.gravity)) === severityLabel
        && normalizeSkill(scenario?.requiredSkills) === skill
      )).length;
      if (count > max) {
        max = count;
      }
      total += count;
      data.push([colIndex, rowIndex, count]);
    });
  });
  return {
    rowLabels: [...W4_LEVEL_LABELS],
    columnLabels,
    data,
    max,
    total,
    hasData: total > 0 && columnLabels.length > 0
  };
};

export const resolveAttackPathId = (attackPath) => String(attackPath?.id ?? attackPath?.pathId ?? '');

// Retained attack paths only — parity with the Workshop 4 ThreatIntelligence
// filter: excluded when explicitly non-retained or in preliminary-study mode.
export const isRetainedAttackPath = (attackPath) => (
  attackPath?.retained !== false && attackPath?.preliminary?.active !== true
);

export const buildCoverage = (attackPaths, operationalScenarios) => {
  const paths = (Array.isArray(attackPaths) ? attackPaths : []).filter(isRetainedAttackPath);
  const scenarios = Array.isArray(operationalScenarios) ? operationalScenarios : [];
  const coveredIds = new Set();
  scenarios.forEach((scenario) => {
    const attackPathId = String(scenario?.attackPathId ?? '');
    if (attackPathId) {
      coveredIds.add(attackPathId);
    }
  });
  let withScenarios = 0;
  paths.forEach((path) => {
    const pathId = resolveAttackPathId(path);
    if (pathId && coveredIds.has(pathId)) {
      withScenarios += 1;
    }
  });
  return {
    withScenarios,
    withoutScenarios: paths.length - withScenarios,
    total: paths.length,
    retainedTotal: paths.length
  };
};
